import { GraphQLClient } from "graphql-request";
import { GATEWAY_URL, blocksHeaders } from "@/src/services/blocks/config";
import { getCachedOrgId, resolveActiveOrgId } from "@/src/services/blocks/session";
import { refreshSession } from "@/src/services/blocks/sso";
import { getActiveOrg } from "./auth.api";
import { isGraphQLAuthError } from "./auth-error";
import { handleAuthFailure, MAX_REFRESH_ATTEMPTS } from "./token-refresh";

/**
 * Blocks v4 data gateway.
 *
 * One endpoint (`POST /data/v4/gateway`) serves every schema in the project.
 * Auth is the hosted SSO session cookie — no bearer token, so requests go out
 * with `credentials: "include"` and a 401 is retried once after renewing the
 * session.
 *
 * Multi-org isolation is enforced by scoping every query to the active org via
 * the typed `where` argument. The legacy JSON `filter` string argument is fully
 * migrated out of the app, so nothing scopes it any more.
 */

/**
 * Org scoping: the platform owns a single `OrganizationId` per record, so it is
 * matched with `eq`. (v1 carried a custom `OrganizationIds` list matched with
 * `contains`; that field does not exist in v4 and querying it errors.)
 */
const orgWhereClause = (orgId: string) => ({ OrganizationId: { eq: orgId } });

const scopeWhereToOrg = (variables: unknown, orgId: string): unknown => {
  if (!variables || typeof variables !== "object") return variables;
  const vars = variables as Record<string, unknown>;

  const where = vars.where;
  if (!where || typeof where !== "object" || Array.isArray(where)) {
    return variables;
  }
  if ("OrganizationId" in (where as Record<string, unknown>)) {
    return variables; // already scoped by the query itself
  }

  return {
    ...vars,
    where: { ...(where as Record<string, unknown>), ...orgWhereClause(orgId) },
  };
};

// NOTE: v1 had an `injectOrgIntoMutation` here that wrote `OrganizationIds:
// [orgId]` into insert inputs (and filled a `$organizationIds` variable). It is
// gone deliberately: in v4 the platform assigns `OrganizationId` itself from the
// session, so writing it client-side is both unnecessary and rejected.

const gqlClient = async () => {
  const client = new GraphQLClient(GATEWAY_URL, {
    headers: blocksHeaders(),
    credentials: "include",
  });

  // Org id comes from the user's stored selection (v4 has no readable token to
  // decode), falling back to their sole organization.
  const orgId =
    getCachedOrgId() ?? (await resolveActiveOrgId(await getActiveOrg()));
  const scoped = !!orgId && orgId !== "default";
  const originalRequest = client.request.bind(client);

  const runWithAuthRetry = async (args: any[]): Promise<unknown> => {
    let attempts = 0;
    for (;;) {
      try {
        return await (originalRequest as (...a: any[]) => unknown)(...args);
      } catch (err: any) {
        if (!isGraphQLAuthError(err)) throw err;

        if (attempts >= MAX_REFRESH_ATTEMPTS) {
          throw err;
        }
        attempts += 1;

        try {
          // Rotates the session cookies; nothing to re-set on the client.
          await refreshSession();
        } catch (refreshErr) {
          await handleAuthFailure();
          throw err;
        }
      }
    }
  };

  (client as { request: (...args: any[]) => unknown }).request = (
    ...args: any[]
  ) => {
    if (scoped) {
      let variables = args.length >= 2 ? args[1] : undefined;
      variables = scopeWhereToOrg(variables, orgId as string);
      args[1] = variables;
    }
    return runWithAuthRetry(args);
  };

  return client;
};

export default gqlClient;
